import { useMutation, useQuery } from '@tanstack/react-query';
import type { DeletionBlockerDto } from 'chery-contracts';
import { useEffect, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Notice } from '../../components/ui/Notice';
import { Spinner } from '../../components/ui/Spinner';
import { errorData, isConflictError, serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { blockersFrom, type KpiRow } from './kpi-display';

const BlockerList = ({ blockers }: { blockers: readonly DeletionBlockerDto[] }) => (
  <ul className="list-disc space-y-1.5 ps-5 text-sm text-foreground">
    {blockers.map((blocker) => (
      <li
        key={blocker.code}
        data-blocker={blocker.code}
      >
        {blocker.message}
      </li>
    ))}
  </ul>
);

export interface DeleteKpiDialogProps {
  kpi: KpiRow | null;
  onClose: () => void;
  onDeleted: (name: string) => void;
  onDeactivateInstead: (kpi: KpiRow) => void;
}

/**
 * The delete guard (plan §3.1). Opening it asks the service what would block the delete (`kpis.get`), so the user
 * sees the exact reasons before confirming; the delete itself re-checks in a serializable transaction and a 409
 * carries the same structured blockers, which replace the preview.
 */
export const DeleteKpiDialog = ({ kpi, onClose, onDeleted, onDeactivateInstead }: DeleteKpiDialogProps) => {
  const { t } = useI18nTranslation();
  const [serverBlockers, setServerBlockers] = useState<DeletionBlockerDto[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    setServerBlockers(null);
    setFailure(null);
  }, [kpi]);

  const detail = useQuery({ ...orpc.kpis.get.queryOptions({ input: { id: kpi?.id ?? '' } }), enabled: kpi !== null, staleTime: 0, gcTime: 0 });
  const remove = useMutation(orpc.kpis.delete.mutationOptions());

  if (!kpi) {
    return null;
  }

  const blockers = serverBlockers ?? detail.data?.deletion.blockers ?? [];
  const deletable = serverBlockers === null && detail.data?.deletion.deletable === true;

  const onConfirm = async (): Promise<void> => {
    setFailure(null);

    try {
      await remove.mutateAsync({ id: kpi.id });
      onDeleted(kpi.name);
    } catch (error) {
      const fromServer = isConflictError(error) ? blockersFrom(errorData(error)) : [];

      if (fromServer.length > 0) {
        setServerBlockers(fromServer);
      } else {
        setFailure(serverMessage(error) ?? t('errors.saveFailed'));
      }
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('kpis.delete.title', { name: kpi.name })}
      description={kpi.code}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          {!deletable && blockers.length > 0 && kpi.status === 'ACTIVE' ? (
            <Button
              variant="outline"
              onClick={() => onDeactivateInstead(kpi)}
            >
              {t('kpis.delete.deactivateInstead')}
            </Button>
          ) : null}
          {deletable ? (
            <Button
              variant="destructive"
              isLoading={remove.isPending}
              onClick={() => void onConfirm()}
            >
              {t('kpis.delete.confirm')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-3">
        {detail.isLoading ? (
          <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner />
            {t('kpis.delete.checking')}
          </p>
        ) : detail.isError && serverBlockers === null ? (
          <Notice tone="danger">{serverMessage(detail.error) ?? t('errors.loadFailed')}</Notice>
        ) : deletable ? (
          <p className="text-sm text-foreground">{t('kpis.delete.allowed')}</p>
        ) : (
          <>
            <p className="text-sm font-medium text-foreground">{t('kpis.delete.blocked')}</p>
            <BlockerList blockers={blockers} />
          </>
        )}
        {failure ? <Notice tone="danger">{failure}</Notice> : null}
      </div>
    </Dialog>
  );
};

export interface DeactivateKpiDialogProps {
  kpi: KpiRow | null;
  onClose: () => void;
  onDeactivated: (name: string) => void;
}

/** Deactivation is never blocked — only acknowledged, with the service's warning about sub-KPIs and dependents. */
export const DeactivateKpiDialog = ({ kpi, onClose, onDeactivated }: DeactivateKpiDialogProps) => {
  const { t } = useI18nTranslation();
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => setFailure(null), [kpi]);

  const detail = useQuery({ ...orpc.kpis.get.queryOptions({ input: { id: kpi?.id ?? '' } }), enabled: kpi !== null, staleTime: 0, gcTime: 0 });
  const update = useMutation(orpc.kpis.update.mutationOptions());

  if (!kpi) {
    return null;
  }

  const onConfirm = async (): Promise<void> => {
    setFailure(null);

    try {
      await update.mutateAsync({ id: kpi.id, status: 'INACTIVE' });
      onDeactivated(kpi.name);
    } catch (error) {
      setFailure(serverMessage(error) ?? t('errors.saveFailed'));
    }
  };

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('kpis.deactivate.title', { name: kpi.name })}
      description={kpi.code}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={update.isPending}
            disabled={detail.isLoading || update.isPending}
            onClick={() => void onConfirm()}
          >
            {t('kpis.deactivate.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-foreground">{t('kpis.deactivate.body')}</p>
        {detail.data?.deactivationWarning ? <Notice>{detail.data.deactivationWarning}</Notice> : null}
        {failure ? <Notice tone="danger">{failure}</Notice> : null}
      </div>
    </Dialog>
  );
};
